import { useState } from "react";

import dayjs from "dayjs";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Mail,
  Plus,
  RefreshCw,
  Send,
  XCircle,
} from "lucide-react";
import Swal from "sweetalert2";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { StatCardsSkeleton, TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { supabase } from "~/integrations/supabase/client";
import { isValidEmail } from "~/utils/helpers";

import {
  useClientsEligibleForReminders,
  useDebtReminders,
} from "./hooks/useDebtReminders";

const DebtRemindersPage = () => {
  const [selectedDate, setSelectedDate] = useState(
    dayjs().format("YYYY-MM-DD")
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [eligibleClientsPage, setEligibleClientsPage] = useState(1);
  const [isSendingBulk, setIsSendingBulk] = useState(false);
  const [activeTab, setActiveTab] = useState<"pending" | "sent">("pending");
  const pageSize = 10;
  const eligiblePageSize = 5;

  const { reminders, totalCount, isLoading, stats } = useDebtReminders(
    selectedDate,
    currentPage,
    pageSize
  );

  const {
    data: eligibleClients,
    isLoading: isLoadingEligible,
    refetch: refetchEligible,
  } = useClientsEligibleForReminders(selectedDate);

  // Use all reminders since search has been removed
  const filteredReminders = reminders;

  // Pagination for eligible clients
  const totalEligibleClients = eligibleClients?.length || 0;
  const totalEligiblePages = Math.ceil(totalEligibleClients / eligiblePageSize);
  const startIndex = (eligibleClientsPage - 1) * eligiblePageSize;
  const endIndex = startIndex + eligiblePageSize;
  const paginatedEligibleClients =
    eligibleClients?.slice(startIndex, endIndex) || [];

  const totalPages = Math.ceil(totalCount / pageSize);

  const eligibleTotalAmount = (eligibleClients || []).reduce(
    (sum, client) => sum + (client.overdue_amount || 0),
    0
  );
  const eligibleAverageDays =
    totalEligibleClients > 0
      ? Math.round(
          (eligibleClients || []).reduce(
            (sum, client) => sum + Math.abs(client.overdue_days || 0),
            0
          ) / totalEligibleClients
        )
      : 0;

  const handleDateChange = (newDate: string) => {
    setSelectedDate(newDate);
    setCurrentPage(1); // Reset to first page when date changes
    setEligibleClientsPage(1); // Reset eligible clients page too
  };

  const handleFacturaClick = async (facturaNumber: string) => {
    try {
      // Find the PDF URL for this factura number
      const { data: document, error } = await supabase
        .from("sales_request_documents")
        .select("url_public_view")
        .eq("bsale_number", facturaNumber)
        .eq("document_type", "factura")
        .single();

      if (error || !document || !document.url_public_view) {
        console.error("Error finding document PDF:", error);
        Swal.fire({
          icon: "warning",
          title: "PDF no encontrado",
          text: "No se pudo encontrar el PDF de la factura",
          confirmButtonColor: "#3b82f6",
        });
        return;
      }

      // Open the PDF in a new tab
      window.open(document.url_public_view, "_blank");
    } catch (error) {
      console.error("Error opening PDF:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al abrir el PDF de la factura",
        confirmButtonColor: "#3b82f6",
      });
    }
  };

  const handleAddEmail = async (clientId: string, clientName: string) => {
    const result = await Swal.fire({
      title: "Agregar Email",
      html: `
        <p>Agregar email para <strong>${clientName}</strong></p>
        <input id="email-input" type="email" class="swal2-input" placeholder="cliente@email.com" style="margin-top: 10px;">
      `,
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#6b7280",
      preConfirm: () => {
        const email = (
          document.getElementById("email-input") as HTMLInputElement
        )?.value;
        if (!email || !isValidEmail(email)) {
          Swal.showValidationMessage("Por favor ingrese un email válido");
          return false;
        }
        return email;
      },
    });

    if (result.isConfirmed && result.value) {
      try {
        const { error } = await supabase
          .from("clients")
          .update({ email: result.value })
          .eq("id", clientId);

        if (error) throw error;

        Swal.fire({
          icon: "success",
          title: "Email agregado",
          text: `Email ${result.value} agregado exitosamente para ${clientName}`,
          confirmButtonColor: "#10b981",
        });

        // Refresh the data
        refetchEligible();
      } catch (error) {
        console.error("Error updating client email:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Error al agregar el email del cliente",
          confirmButtonColor: "#ef4444",
        });
      }
    }
  };

  const handleResendEmail = async (reminder: any) => {
    try {
      // Call the send-consolidated-debt-reminder function
      const response = await fetch(
        `https://mdrvhekhimhcwydrpueo.supabase.co/functions/v1/send-consolidated-debt-reminder`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1kcnZoZWtoaW1oY3d5ZHJwdWVvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDM1MzcyOTksImV4cCI6MjA1OTExMzI5OX0.XYVbRbA5VhJKv8p5Fe64oXfzeaoY7fMVPDfJlNXqElY"}`,
          },
          body: JSON.stringify({
            client_id: reminder.client_id,
            cutoff_date: selectedDate,
            test_mode: false, // Use real client emails
          }),
        }
      );

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "Email reenviado",
          text: `Email enviado exitosamente para ${reminder.client_name}`,
          confirmButtonColor: "#10b981",
        });
      } else {
        const errorText = await response.text();
        Swal.fire({
          icon: "error",
          title: "Error al reenviar",
          text: `Error al reenviar email: ${errorText}`,
          confirmButtonColor: "#ef4444",
        });
      }
    } catch (error) {
      console.error("Error resending email:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al reenviar email",
        confirmButtonColor: "#ef4444",
      });
    }
  };

  const handleSendIndividualReminder = async (client: any) => {
    try {
      const result = await Swal.fire({
        icon: "question",
        title: "Confirmar envío",
        html: `
          <p>¿Enviar recordatorio a <strong>${client.client_name}</strong>?</p>
          <div style="margin: 1rem 0; padding: 1rem; background-color: #f3f4f6; border-radius: 0.5rem; text-align: left;">
            <strong>Detalles:</strong><br>
            • Email: ${client.client_email}<br>
            • Tipo: ${getReminderTypeLabel(client.reminder_type)}<br>
            • Monto: $${(client.overdue_amount || 0).toLocaleString()}
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: "Enviar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#10b981",
        cancelButtonColor: "#6b7280",
      });

      if (!result.isConfirmed) return;

      const response = await fetch(
        `https://mdrvhekhimhcwydrpueo.supabase.co/functions/v1/send-consolidated-debt-reminder`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1kcnZoZWtoaW1oY3d5ZHJwdWVvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDM1MzcyOTksImV4cCI6MjA1OTExMzI5OX0.XYVbRbA5VhJKv8p5Fe64oXfzeaoY7fMVPDfJlNXqElY"}`,
          },
          body: JSON.stringify({
            client_id: client.client_id,
            cutoff_date: selectedDate,
            test_mode: false,
          }),
        }
      );

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "Email enviado",
          text: `Recordatorio enviado exitosamente a ${client.client_name}`,
          confirmButtonColor: "#10b981",
        });
        refetchEligible();
      } else {
        const errorText = await response.text();
        Swal.fire({
          icon: "error",
          title: "Error al enviar",
          text: `Error al enviar recordatorio: ${errorText}`,
          confirmButtonColor: "#ef4444",
        });
      }
    } catch (error) {
      console.error("Error sending individual reminder:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al enviar recordatorio",
        confirmButtonColor: "#ef4444",
      });
    }
  };

  const handleBulkSendReminders = async () => {
    if (!eligibleClients || eligibleClients.length === 0) {
      Swal.fire({
        icon: "info",
        title: "Sin clientes elegibles",
        text: "No hay clientes elegibles para enviar recordatorios",
        confirmButtonColor: "#3b82f6",
      });
      return;
    }

    // Show detailed confirmation with client breakdown
    const reminderTypeBreakdown = eligibleClients.reduce(
      (acc, client) => {
        acc[client.reminder_type] = (acc[client.reminder_type] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    const result = await Swal.fire({
      icon: "question",
      title: "Confirmar envío de recordatorios",
      html: `
        <p>¿Está seguro de enviar recordatorios a <strong>${eligibleClients.length}</strong> cliente${eligibleClients.length > 1 ? "s" : ""}?</p>
        <div style="margin: 1rem 0; padding: 1rem; background-color: #f3f4f6; border-radius: 0.5rem; text-align: left;">
          <strong>Desglose:</strong><br>
          ${Object.entries(reminderTypeBreakdown)
            .map(([type, count]) => `• ${count} ${getReminderTypeLabel(type)}`)
            .join("<br>")}
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Sí, enviar recordatorios",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#6b7280",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    setIsSendingBulk(true);
    let successCount = 0;
    let errorCount = 0;
    const startTime = Date.now();

    for (let i = 0; i < eligibleClients.length; i++) {
      const client = eligibleClients[i];
      try {
        const response = await fetch(
          `https://mdrvhekhimhcwydrpueo.supabase.co/functions/v1/send-consolidated-debt-reminder`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1kcnZoZWtoaW1oY3d5ZHJwdWVvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDM1MzcyOTksImV4cCI6MjA1OTExMzI5OX0.XYVbRbA5VhJKv8p5Fe64oXfzeaoY7fMVPDfJlNXqElY"}`,
            },
            body: JSON.stringify({
              client_id: client.client_id,
              cutoff_date: selectedDate,
              test_mode: false,
            }),
          }
        );

        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
          console.error(
            `Error sending to ${client.client_name}:`,
            await response.text()
          );
        }

        // Small delay between requests to avoid overwhelming the server
        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (error) {
        errorCount++;
        console.error(`Error sending to ${client.client_name}:`, error);
      }
    }

    const endTime = Date.now();
    const duration = Math.round((endTime - startTime) / 1000);
    setIsSendingBulk(false);

    // Refresh data after sending
    refetchEligible();

    const successText = `${successCount} email${successCount !== 1 ? "s" : ""} enviado${successCount !== 1 ? "s" : ""} exitosamente`;
    const errorText =
      errorCount > 0
        ? `${errorCount} error${errorCount !== 1 ? "es" : ""}`
        : "";

    Swal.fire({
      icon: errorCount > 0 ? "warning" : "success",
      title: "Proceso completado",
      html: `
        <div style="text-align: left;">
          <p><strong>⏱️ Duración:</strong> ${duration}s</p>
          <p><strong>✅ Exitosos:</strong> ${successText}</p>
          ${errorText ? `<p><strong>❌ Errores:</strong> ${errorText}</p>` : ""}
        </div>
      `,
      confirmButtonColor: errorCount > 0 ? "#f59e0b" : "#10b981",
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "sent":
        return <CheckCircle className="h-4 w-4 text-emerald-600" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-red-600" />;
      case "pending":
        return <Clock className="h-4 w-4 text-amber-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getReminderTypeLabel = (type: string) => {
    switch (type) {
      case "5_days_before":
        return "5 días antes del vencimiento";
      case "5_days_overdue":
        return "5 días vencida";
      case "28_days_overdue_non_fincred":
        return "28 días vencida (no FINCRED)";
      case "12_days_overdue_fincred":
        return "12 días vencida (FINCRED)";
      case "daily_overdue":
        return "Vencida (general)";
      // Legacy types for backward compatibility
      case "pre_expiration":
        return "Próximo a vencer";
      case "early_overdue":
        return "Recién vencido";
      case "final_notice":
        return "Aviso final";
      default:
        return type;
    }
  };

  // Clean, low-saturation chips: subtle tinted text on a light background.
  const getReminderTypeBadge = (type: string) => {
    switch (type) {
      case "5_days_before":
        return "border-blue-200 bg-blue-50 text-blue-700";
      case "5_days_overdue":
        return "border-amber-200 bg-amber-50 text-amber-700";
      case "28_days_overdue_non_fincred":
        return "border-red-200 bg-red-50 text-red-700";
      case "12_days_overdue_fincred":
        return "border-red-200 bg-red-50 text-red-700";
      case "daily_overdue":
        return "border-violet-200 bg-violet-50 text-violet-700";
      // Legacy types for backward compatibility
      case "pre_expiration":
        return "border-blue-200 bg-blue-50 text-blue-700";
      case "early_overdue":
        return "border-amber-200 bg-amber-50 text-amber-700";
      case "final_notice":
        return "border-red-200 bg-red-50 text-red-700";
      default:
        return "border-border bg-muted text-muted-foreground";
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "sent":
        return "border-emerald-200 bg-emerald-50 text-emerald-700";
      case "failed":
        return "border-red-200 bg-red-50 text-red-700";
      case "pending":
        return "border-amber-200 bg-amber-50 text-amber-700";
      default:
        return "border-border bg-muted text-muted-foreground";
    }
  };

  const overdueDaysLabel = (days: number) =>
    days > 0 ? `${days} días` : `${Math.abs(days)} días vencido`;

  const overdueDaysClass = (days: number) =>
    days < 0
      ? "font-medium text-red-600"
      : days <= 5
        ? "font-medium text-amber-600"
        : "text-foreground";

  return (
    <PageContainer>
      <PageHeader
        title="Recordatorios de Deuda"
        description="Monitoreo de recordatorios enviados por el sistema automatizado"
        actions={
          <Button
            onClick={handleBulkSendReminders}
            disabled={
              isSendingBulk || !eligibleClients || eligibleClients.length === 0
            }
            className="h-9"
          >
            {isSendingBulk ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Enviar Recordatorios ({eligibleClients?.length || 0})
              </>
            )}
          </Button>
        }
      />

      {/* Cutoff date */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-muted-foreground">
          Fecha de corte:
        </span>
        <Input
          type="date"
          value={selectedDate}
          onChange={(e) => handleDateChange(e.target.value)}
          className="h-9 w-auto"
        />
      </div>

      {/* Tabs for Pending vs Sent Notifications */}
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as "pending" | "sent")}
        className="space-y-6"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="pending" className="gap-2">
            <Clock className="h-4 w-4 shrink-0" />
            <span className="truncate">Pendientes</span>
          </TabsTrigger>
          <TabsTrigger value="sent" className="gap-2">
            <CheckCircle className="h-4 w-4 shrink-0" />
            <span className="truncate">Enviadas</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-6">
          {isLoadingEligible ? (
            <>
              <StatCardsSkeleton count={3} className="lg:grid-cols-3" />
              <TableSkeleton rows={5} columns={7} />
            </>
          ) : !eligibleClients || eligibleClients.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <CheckCircle className="mb-3 h-12 w-12 text-emerald-500" />
                <p className="font-medium text-foreground">
                  No hay clientes pendientes de notificación para esta fecha
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Todos los recordatorios han sido procesados
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* KPI cards */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">
                      Clientes Elegibles
                    </p>
                    <p className="mt-1 text-2xl font-semibold text-foreground">
                      {totalEligibleClients}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Monto Total</p>
                    <p className="mt-1 text-2xl font-semibold text-foreground">
                      ${eligibleTotalAmount.toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
                <Card className="col-span-2 lg:col-span-1">
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">
                      Promedio Días Vencido
                    </p>
                    <p className="mt-1 text-2xl font-semibold text-foreground">
                      {eligibleAverageDays}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Eligible clients — desktop table */}
              <Card className="hidden md:block">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap">
                          Cliente
                        </TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead className="whitespace-nowrap">
                          Tipo
                        </TableHead>
                        <TableHead className="whitespace-nowrap">
                          Monto Factura
                        </TableHead>
                        <TableHead className="whitespace-nowrap">
                          Días
                        </TableHead>
                        <TableHead className="whitespace-nowrap">
                          Factura
                        </TableHead>
                        <TableHead className="whitespace-nowrap text-center">
                          Acciones
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedEligibleClients.map((client, index) => (
                        <TableRow
                          key={client.client_id}
                          className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                          style={{
                            animationDelay: `${Math.min(index * 40, 400)}ms`,
                          }}
                        >
                          <TableCell>
                            <button
                              onClick={() =>
                                window.open(
                                  `/clients/${client.client_id}`,
                                  "_blank"
                                )
                              }
                              className="text-left text-sm font-medium text-primary hover:underline"
                            >
                              {client.client_name}
                            </button>
                          </TableCell>
                          <TableCell>
                            {client.client_email ? (
                              <span className="text-sm text-muted-foreground">
                                {client.client_email}
                              </span>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleAddEmail(
                                    client.client_id,
                                    client.client_name
                                  )
                                }
                                className="h-8 gap-1 text-xs"
                              >
                                <Plus className="h-3 w-3" />
                                Agregar Email
                              </Button>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={getReminderTypeBadge(
                                client.reminder_type
                              )}
                            >
                              {getReminderTypeLabel(client.reminder_type)}
                            </Badge>
                          </TableCell>
                          <TableCell className="whitespace-nowrap font-medium text-red-600">
                            ${(client.overdue_amount || 0).toLocaleString()}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <div className="flex items-center gap-1">
                              {client.overdue_days < 0 && (
                                <AlertCircle className="h-4 w-4 text-red-500" />
                              )}
                              <span
                                className={overdueDaysClass(
                                  client.overdue_days
                                )}
                              >
                                {overdueDaysLabel(client.overdue_days)}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {client.most_urgent_factura_number ? (
                              <button
                                onClick={() =>
                                  handleFacturaClick(
                                    client.most_urgent_factura_number!
                                  )
                                }
                                className="text-sm font-medium text-primary hover:underline"
                              >
                                {client.most_urgent_factura_number}
                              </button>
                            ) : (
                              <span className="text-sm text-muted-foreground">
                                -
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleSendIndividualReminder(client)
                              }
                              className="h-8 gap-1 text-xs"
                              title="Enviar recordatorio individual"
                            >
                              <Send className="h-3 w-3" />
                              Enviar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Pagination for eligible clients */}
                  {totalEligibleClients > eligiblePageSize && (
                    <div className="flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="text-sm text-muted-foreground">
                        Mostrando {startIndex + 1} -{" "}
                        {Math.min(endIndex, totalEligibleClients)} de{" "}
                        {totalEligibleClients} cliente
                        {totalEligibleClients > 1 ? "s" : ""}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9"
                          onClick={() =>
                            setEligibleClientsPage(eligibleClientsPage - 1)
                          }
                          disabled={eligibleClientsPage <= 1}
                        >
                          Anterior
                        </Button>
                        <span className="text-sm text-muted-foreground">
                          Página {eligibleClientsPage} de {totalEligiblePages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9"
                          onClick={() =>
                            setEligibleClientsPage(eligibleClientsPage + 1)
                          }
                          disabled={eligibleClientsPage >= totalEligiblePages}
                        >
                          Siguiente
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Eligible clients — mobile card list */}
              <div className="space-y-3 md:hidden">
                {paginatedEligibleClients.map((client) => (
                  <Card key={client.client_id}>
                    <CardContent className="space-y-3 p-4">
                      <div className="flex items-start justify-between gap-2">
                        <button
                          onClick={() =>
                            window.open(
                              `/clients/${client.client_id}`,
                              "_blank"
                            )
                          }
                          className="text-left text-sm font-semibold text-primary hover:underline"
                        >
                          {client.client_name}
                        </button>
                        <Badge
                          variant="outline"
                          className={getReminderTypeBadge(client.reminder_type)}
                        >
                          {getReminderTypeLabel(client.reminder_type)}
                        </Badge>
                      </div>

                      {client.client_email ? (
                        <p className="break-all text-sm text-muted-foreground">
                          {client.client_email}
                        </p>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleAddEmail(client.client_id, client.client_name)
                          }
                          className="h-8 gap-1 text-xs"
                        >
                          <Plus className="h-3 w-3" />
                          Agregar Email
                        </Button>
                      )}

                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Monto Factura
                          </p>
                          <p className="font-medium text-red-600">
                            ${(client.overdue_amount || 0).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Días</p>
                          <p className={overdueDaysClass(client.overdue_days)}>
                            {overdueDaysLabel(client.overdue_days)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Factura
                          </p>
                          {client.most_urgent_factura_number ? (
                            <button
                              onClick={() =>
                                handleFacturaClick(
                                  client.most_urgent_factura_number!
                                )
                              }
                              className="text-sm font-medium text-primary hover:underline"
                            >
                              {client.most_urgent_factura_number}
                            </button>
                          ) : (
                            <span className="text-sm text-muted-foreground">
                              -
                            </span>
                          )}
                        </div>
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSendIndividualReminder(client)}
                        className="h-9 w-full gap-1"
                      >
                        <Send className="h-4 w-4" />
                        Enviar recordatorio
                      </Button>
                    </CardContent>
                  </Card>
                ))}

                {/* Pagination for eligible clients (mobile) */}
                {totalEligibleClients > eligiblePageSize && (
                  <div className="flex flex-col gap-3 rounded-lg border bg-card px-4 py-3">
                    <div className="text-sm text-muted-foreground">
                      Mostrando {startIndex + 1} -{" "}
                      {Math.min(endIndex, totalEligibleClients)} de{" "}
                      {totalEligibleClients} cliente
                      {totalEligibleClients > 1 ? "s" : ""}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9"
                        onClick={() =>
                          setEligibleClientsPage(eligibleClientsPage - 1)
                        }
                        disabled={eligibleClientsPage <= 1}
                      >
                        Anterior
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        {eligibleClientsPage} / {totalEligiblePages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9"
                        onClick={() =>
                          setEligibleClientsPage(eligibleClientsPage + 1)
                        }
                        disabled={eligibleClientsPage >= totalEligiblePages}
                      >
                        Siguiente
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="sent" className="space-y-6">
          {/* Stats Cards */}
          {isLoading ? (
            <StatCardsSkeleton count={4} />
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
              <Card>
                <CardContent className="flex items-center gap-3 p-4">
                  <Mail className="h-8 w-8 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-semibold text-foreground">
                      {stats?.total_processed || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Total Procesados
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="flex items-center gap-3 p-4">
                  <CheckCircle className="h-8 w-8 text-emerald-600" />
                  <div>
                    <p className="text-2xl font-semibold text-foreground">
                      {stats?.sent_count || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">Enviados</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="flex items-center gap-3 p-4">
                  <XCircle className="h-8 w-8 text-red-600" />
                  <div>
                    <p className="text-2xl font-semibold text-foreground">
                      {stats?.failed_count || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">Fallidos</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="flex items-center gap-3 p-4">
                  <Clock className="h-8 w-8 text-amber-600" />
                  <div>
                    <p className="text-2xl font-semibold text-foreground">
                      {stats?.pending_count || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">Pendientes</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Reminders Table */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">
                Recordatorios del {dayjs(selectedDate).format("DD/MM/YYYY")}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="p-4">
                  <TableSkeleton rows={6} columns={5} />
                </div>
              ) : filteredReminders.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Mail className="mb-3 h-12 w-12 text-muted-foreground/50" />
                  <p className="text-muted-foreground">
                    No hay recordatorios para esta fecha
                  </p>
                </div>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">
                            Estado
                          </TableHead>
                          <TableHead className="whitespace-nowrap">
                            Cliente
                          </TableHead>
                          <TableHead className="whitespace-nowrap">
                            Tipo
                          </TableHead>
                          <TableHead className="whitespace-nowrap">
                            Procesado
                          </TableHead>
                          <TableHead className="whitespace-nowrap text-center">
                            Acciones
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredReminders.map((reminder, index) => (
                          <TableRow
                            key={reminder.id}
                            className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                            style={{
                              animationDelay: `${Math.min(index * 40, 400)}ms`,
                            }}
                          >
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getStatusIcon(reminder.status)}
                                <Badge
                                  variant="outline"
                                  className={getStatusBadge(reminder.status)}
                                >
                                  {reminder.status === "sent"
                                    ? "Enviado"
                                    : reminder.status === "failed"
                                      ? "Fallido"
                                      : reminder.status === "pending"
                                        ? "Pendiente"
                                        : reminder.status}
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell>
                              <button
                                onClick={() =>
                                  window.open(
                                    `/clients/${reminder.client_id}`,
                                    "_blank"
                                  )
                                }
                                className="text-left font-medium text-primary hover:underline"
                              >
                                {reminder.client_name}
                              </button>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={getReminderTypeBadge(
                                  reminder.reminder_type
                                )}
                              >
                                {getReminderTypeLabel(reminder.reminder_type)}
                              </Badge>
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                              {reminder.processed_at
                                ? dayjs(reminder.processed_at).format(
                                    "HH:mm:ss"
                                  )
                                : "-"}
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleResendEmail(reminder)}
                                className="h-8 gap-1 text-xs"
                                title="Reenviar email"
                              >
                                <Send className="h-3 w-3" />
                                Reenviar
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile card list */}
                  <div className="space-y-3 p-4 md:hidden">
                    {filteredReminders.map((reminder) => (
                      <div
                        key={reminder.id}
                        className="space-y-3 rounded-lg border bg-card p-4"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <button
                            onClick={() =>
                              window.open(
                                `/clients/${reminder.client_id}`,
                                "_blank"
                              )
                            }
                            className="text-left text-sm font-semibold text-primary hover:underline"
                          >
                            {reminder.client_name}
                          </button>
                          <div className="flex items-center gap-1.5">
                            {getStatusIcon(reminder.status)}
                            <Badge
                              variant="outline"
                              className={getStatusBadge(reminder.status)}
                            >
                              {reminder.status === "sent"
                                ? "Enviado"
                                : reminder.status === "failed"
                                  ? "Fallido"
                                  : reminder.status === "pending"
                                    ? "Pendiente"
                                    : reminder.status}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge
                            variant="outline"
                            className={getReminderTypeBadge(
                              reminder.reminder_type
                            )}
                          >
                            {getReminderTypeLabel(reminder.reminder_type)}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {reminder.processed_at
                              ? dayjs(reminder.processed_at).format("HH:mm:ss")
                              : "-"}
                          </span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleResendEmail(reminder)}
                          className="h-9 w-full gap-1"
                        >
                          <Send className="h-4 w-4" />
                          Reenviar
                        </Button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Pagination */}
              {totalCount > 0 && (
                <div className="flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-muted-foreground">
                    Mostrando {(currentPage - 1) * pageSize + 1} -{" "}
                    {Math.min(currentPage * pageSize, totalCount)} de{" "}
                    {totalCount} recordatorio{totalCount > 1 ? "s" : ""}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage <= 1}
                    >
                      Anterior
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Página {currentPage} de {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
};

export default DebtRemindersPage;
